import * as React from "react";
import { useState, useEffect } from "react";
// import { useHistory } from "react-router-dom";
import { Button, Text } from "@fluentui/react-components";
import { Box } from "@mui/material";

import { useFilterStyles } from "../styles/filterStyles";
import SpreetailHeader from "../layout/SpreetailHeader";
import SpreetailLoader from "../common/SpreetailLoader";
import { fetchFilteredAdvertisingData } from "../../../shared/mockData";
import { generateMarketingDashboard } from "../../marketing";
import { refreshAdvertisingRawDataSheet } from "../../rowdata";
import DateRangeFilter from "../filters/controls/DateRangeFilter";
import BrandFilter from "../filters/controls/BrandFilter";
import SkusFilter from "../filters/controls/SkusFilter";
import KeywordFilter from "../filters/controls/KeywordFilter";
import CampaignTypeFilter from "../filters/controls/CampaignTypeFilter";
import { useInterdependentAdvertisingFilters } from "../../hooks/useInterdependentAdvertisingFilters";
import {
  updateAdvertisingFilterStateForSheet,
  migrateAdvertisingFilterState,
} from "../../../shared/filterStateManager";
import { isRawDataSheet } from "../../rowdata";

const CampaignPerformanceRoute: React.FC = () => {
  const filterStyles = useFilterStyles();



  const {
    startDate,
    endDate,
    selected,
    options: interOptions,
    isLoading: optionsLoading,
    error: optionsError,
    activationOrder,
    setStartDate,
    setEndDate,
    setBrand: setBrandInter,
    setSkus: setSkusInter,
    setKeyword: setKeywordInter,
    setCampaign: setCampaignInter,
    activate,
    clear: clearFilters,
    clearFromLevel,
  } = useInterdependentAdvertisingFilters();

  // Loading state for dashboard generation
  const [isGenerating, setIsGenerating] = useState(false);

  const brand = selected.brand;
  const skus = selected.skus;
  const keyword = selected.keyword;
  const campaign = selected.campaign;
  const channel = "all";
  const hasActiveFilters = Boolean(
    startDate || endDate || brand.length || skus.length || keyword.length || campaign.length
  );

  // Helper function to get the level for each filter (only if it has selected values)
  const getFilterLevel = (filterName: "brand" | "skus" | "keyword" | "campaign") => {
    const index = activationOrder.indexOf(filterName);
    if (index < 0) return undefined;

    // Only show level if the filter has actual selected values
    const hasValues = (() => {
      switch (filterName) {
        case "brand":
          return selected.brand.length > 0;
        case "skus":
          return selected.skus.length > 0;
        case "keyword":
          return selected.keyword.length > 0;
        case "campaign":
          return selected.campaign.length > 0;
        default:
          return false;
      }
    })();

    const level = hasValues ? index + 1 : undefined;

    // Debug logging
    if (filterName === "campaign") {
      console.log("🔍 Campaign Filter Debug:", {
        filterName,
        activationOrder,
        index,
        hasValues,
        selectedValues: selected.campaign,
        level,
      });
    }

    return level;
  };

  // Success state to show feedback
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  const handleClearFilters = async () => {
    await clearFilters();
    setShowSuccess(false);
    setErrorMessage(null);

    // Show a brief success message
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  };

  // Removed Close button: navigating home is now handled via header logo click

  const handleApplyFilter = async () => {
    console.log("🚀 APPLYING ADVERTISING DASHBOARD FILTER");
    setIsGenerating(true);
    setErrorMessage(null);
  
    try {
      // Build the filters object for the API
      const filters: any = {};
  
      if (startDate && endDate) {
        filters.startDate = startDate.toISOString().split("T")[0];
        filters.endDate = endDate.toISOString().split("T")[0];
      }
  
      if (brand && brand.length > 0) {
        filters.brands = brand;
      }
  
      if (skus && skus.length > 0) {
        filters.asins = skus;
      }
  
      if (channel && channel !== "all") {
        filters.channels = [channel];
      }
  
      if (campaign && campaign.length > 0) {
        filters.campaign_name = campaign;
      }
  
      if (keyword && keyword.length > 0) {
        filters.keyword_contains = keyword;
      }
  
      // Fetch filtered data with the properly constructed filters object
      const filteredData = await fetchFilteredAdvertisingData(filters);
      console.log("Filtered data received:", filteredData);
  
      // Check if data is empty before proceeding
      if (!filteredData || !filteredData.data || filteredData.data.length === 0) {
        setErrorMessage("No data available for the selected filters. Please try adjusting your date range or filter criteria.");
        return;
      }

      if (filteredData.row_count && filteredData.row_count > 50000) {
        setErrorMessage(`Filtering has returned more than 50,000 rows (${filteredData.row_count}). Please refine your filters.`);
        return;
      }
  
      const filterParams = {
        startDate: startDate,
        endDate: endDate,
        brand,
        skus,
        channel,
        keyword,
        campaign,
      };
  
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("name");
        await context.sync();
  
        const currentName = activeSheet.name;
        const isRawData = isRawDataSheet(currentName);
  
        console.log("💾 About to save advertising filters for sheet:", {
          currentName,
          filterParams,
        });  
        // Save filters to custom properties
        await updateAdvertisingFilterStateForSheet(currentName, filterParams);
  
        if (isRawData) {
          // Refresh raw data sheet
          await refreshAdvertisingRawDataSheet(context, activeSheet);
        } else {
          // Generate new dashboard
          await generateMarketingDashboard(filteredData, filterParams);
        }
      });
  
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error: any) {
      console.error("❌ Error applying filters:", error);
      // Check for Excel-specific errors and convert to user-friendly messages
      const errorMessage = error.message || "";
      if (errorMessage.includes("number of rows or columns") || errorMessage.includes("doesn't match the size")) {
        setErrorMessage("No data available for the selected filters. Please try adjusting your date range or filter criteria.");
      } else if (errorMessage.includes("No data available")) {
        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(errorMessage || "An unknown error occurred. Please try again.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (isGenerating) {
    return (
      <div
        className={filterStyles.filterContainer}
        style={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SpreetailHeader />
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
          }}
        >
          <SpreetailLoader
            title="Working on your data"
            subtitle="Please wait while we generate your data"
            size="large"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={filterStyles.filterContainer} style={{ height: "100%", minHeight: 0 }}>
      <SpreetailHeader />
      <div
        className={filterStyles.filterContent}
        style={{ overflowY: "auto", flex: 1, minHeight: 0, padding: "0 16px" }}
      >
        <div className={filterStyles.filterHeader}>
          <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>
            Advertising Data Filter
          </Text>
          <Text size={200} style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}>
            Apply filters to refine your Advertising Data
          </Text>
          {errorMessage && (
            <div
              style={{
                marginTop: "8px",
                padding: "12px 14px",
                borderRadius: "10px",
                border: "1px solid rgba(251,146,60,0.35)",
                background: "rgba(251,146,60,0.08)",
                display: "flex",
                gap: "10px",
                alignItems: "flex-start",
                boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
                backdropFilter: "blur(6px)",
              }}
            >
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  background: "rgba(249,115,22,0.9)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "12px",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              >
                !
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#9A3412",
                  lineHeight: 1.5,
                }}
              >
                {errorMessage}
              </p>
            </div>
          )}
        </div>

        {/* Top Action Buttons - Apply to the left of Clear */}
        <div
          style={{
            padding: "0 0 16px 0",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <button
            onClick={handleApplyFilter}
            disabled={isGenerating}
            style={{
              backgroundColor: "rgba(16,185,129,0.12)",
              border: "1.5px solid rgba(16,185,129,0.35)",
              borderRadius: "8px",
              color: "#065F46",
              fontSize: "11px",
              fontWeight: 500,
              padding: "4px 8px",
              textTransform: "none",
              height: "28px",
              lineHeight: 1.2,
              minWidth: "auto",
              cursor: isGenerating ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              opacity: isGenerating ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.12)";
            }}
          >
            {isGenerating ? "Applying Filters..." : "Apply Filters"}
          </button>

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              style={{
                backgroundColor: "rgba(239,68,68,0.10)",
                border: "1.5px solid rgba(239,68,68,0.35)",
                borderRadius: "8px",
                color: "#7F1D1D",
                fontSize: "11px",
                fontWeight: 500,
                padding: "4px 8px",
                textTransform: "none",
                height: "28px",
                lineHeight: 1.2,
                minWidth: "auto",
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.16)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.10)";
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
        {/* Date Range */}
        <Box className={filterStyles.inputGroup} sx={{ width: "100%", maxWidth: "100%" }}>
          <label className={filterStyles.labelText}>Date Range</label>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onChangeStart={setStartDate}
            onChangeEnd={setEndDate}
          />
        </Box>

        {/* Brand */}
        <Box className={filterStyles.inputGroup} sx={{ width: "100%", maxWidth: "100%" }}>
          <BrandFilter
            value={selected.brand}
            onChange={setBrandInter}
            options={interOptions.brands}
            onActivate={() => activate("brand")}
            level={getFilterLevel("brand")}
            onClearLevel={clearFromLevel}
            isLoading={optionsLoading}
          />
        </Box>

        {/* SKUs */}
        <Box className={filterStyles.inputGroup} sx={{ width: "100%", maxWidth: "100%" }}>
          <SkusFilter
            value={selected.skus}
            onChange={setSkusInter}
            options={interOptions.asins}
            onActivate={() => activate("skus")}
            level={getFilterLevel("skus")}
            onClearLevel={clearFromLevel}
            isLoading={optionsLoading}
          />
        </Box>

        {/* Keyword */}
        <Box className={filterStyles.inputGroup} sx={{ width: "100%", maxWidth: "100%" }}>
          <KeywordFilter
            value={selected.keyword}
            onChange={setKeywordInter}
            options={interOptions.keywords}
            onActivate={() => activate("keyword")}
            level={getFilterLevel("keyword")}
            onClearLevel={clearFromLevel}
            isLoading={optionsLoading}
          />
        </Box>

        {/* Campaign Type */}
        <Box className={filterStyles.inputGroup} sx={{ width: "100%", maxWidth: "100%" }}>
          <CampaignTypeFilter
            value={selected.campaign}
            onChange={setCampaignInter}
            options={interOptions.campaigns}
            onActivate={() => activate("campaign")}
            level={getFilterLevel("campaign")}
            onClearLevel={clearFromLevel}
            isLoading={optionsLoading}
          />
        </Box>

        {/* Action Buttons moved to header area above */}
      </div>
    </div>
  );
};

export default CampaignPerformanceRoute;
